import React from "react";
import { Table } from "@tanstack/react-table";
import { MtApiResponse, MtData } from "../../utils/types";

interface MtTablePaginationProps {
  table: Table<MtData>;
  data: MtApiResponse | undefined;
  setPageIndex: (pageIndex: number) => void;
}

const MtTablePagination: React.FC<MtTablePaginationProps> = ({
  table,
  data,
  setPageIndex,
}) => {
  if (!data || data.last_page <= 1) {
    return null;
  }

  const handlePreviousPage = () => {
    if (table.getCanPreviousPage()) {
      const newPageIndex = table.getState().pagination.pageIndex - 1;
      setPageIndex(newPageIndex);
    }
  };

  const handleNextPage = () => {
    if (table.getCanNextPage()) {
      const newPageIndex = table.getState().pagination.pageIndex + 1;
      setPageIndex(newPageIndex);
    }
  };

  const handlePageClick = (pageNumber: number) => {
    setPageIndex(pageNumber - 1); // Convert to 0-based index
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const currentPage = data.current_page;
    const totalPages = data.last_page;
    const pages = [];

    // Always show first page
    if (totalPages > 0) {
      pages.push(1);
    }

    // Add ellipsis if there's a gap
    if (currentPage > 4) {
      pages.push(-1); // -1 represents ellipsis
    }

    // Add pages around current page
    for (let i = Math.max(2, currentPage - 2); i <= Math.min(totalPages - 1, currentPage + 2); i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    // Add ellipsis if there's a gap
    if (currentPage < totalPages - 3) {
      pages.push(-2); // -2 represents ellipsis
    }

    // Always show last page if more than 1 page
    if (totalPages > 1 && !pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Pagination Controls */}
        <div className="flex items-center gap-2">
          {/* Previous Button */}
          <button
            onClick={handlePreviousPage}
            disabled={!table.getCanPreviousPage()}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
              table.getCanPreviousPage()
                ? "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                : "text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed"
            }`}
          >
            Previous
          </button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1">
            {getPageNumbers().map((pageNum, index) => {
              if (pageNum === -1 || pageNum === -2) {
                return (
                  <span key={`ellipsis-${index}`} className="px-2 py-2 text-gray-400">
                    ...
                  </span>
                );
              }

              const isActive = pageNum === data.current_page;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageClick(pageNum)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isActive
                      ? "bg-blue-600 text-white border border-blue-600"
                      : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            onClick={handleNextPage}
            disabled={!table.getCanNextPage()}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
              table.getCanNextPage()
                ? "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                : "text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed"
            }`}
          >
            Next
          </button>
        </div>

        {/* Pagination Info */}
        <div className="text-sm text-gray-700 flex flex-col sm:flex-row items-center gap-2">
          <span>
            Page {data.current_page} of {data.last_page}
          </span>
          <span className="hidden sm:inline">•</span>
          <span>
            Showing {data.from} to {data.to} of {data.total} entries
          </span>
        </div>
      </div>

      {/* Mobile-friendly page info */}
      <div className="mt-3 sm:hidden">
        <div className="text-xs text-gray-600 text-center">
          {data.from}-{data.to} of {data.total} results
        </div>
      </div>
    </div>
  );
};

export default MtTablePagination;